import { ClipboardCheck } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { toast } from 'react-toastify'
import { useQuizListByBranchId } from '@/api/queries/direcionada/administration/quiz-queries'
import { useCreateDirecionada } from '@/api/queries/direcionada/direcionada-queries'
import { useOrganization } from '@/api/queries/organization-queries'
import { usePeople } from '@/api/queries/people-queries'
import { Form } from '@/components/forms/form'
import { FormField } from '@/components/forms/form-field'
import { FormItem } from '@/components/forms/form-item'
import {
	DirectedFreeTextQuestionCard,
	DirectedQuestionCard,
	INSPECTION_COLORS,
	InspectionCard,
	InspectionCardFooter,
	InspectionCardHeader,
	InspectionDateField,
} from '@/components/inspections'
import { CardContent } from '@/components/ui/card'
import { FieldContent } from '@/components/ui/field'
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select'
import { Separator } from '@/components/ui/separator'
import { Textarea } from '@/components/ui/textarea'
import { ROUTES } from '@/constants/routes'
import { useDirecionadaForm } from '@/hooks/forms/use-direcionada-form'
import { useFormErrorFocus } from '@/hooks/forms/use-form-error-focus'
import type {
	DirecionadaFormData,
	DirecionadaInput,
} from '@/schemas/direcionada-schema'
import { useProfileStore } from '@/store'
import type { QuizKind } from '@/types/quiz'
import {
	getApiErrorMessage,
	isPayloadTooLargeApiError,
} from '@/utils/api-error'
import { formatDateYMD, parseStringToDateForDisplay } from '@/utils/formatDate'

/* ────────────────────────────── Types ────────────────────────────── */

interface Question {
	id: string
	title: string
	imagePath?: string
}

interface Questionnaire {
	id: string
	name: string
	kind: QuizKind
	questions: Question[]
}

/* ────────────────────────── Main Page ────────────────────────────── */

export const DirectedInspectionPage = () => {
	const navigate = useNavigate()
	const { t } = useTranslation()
	const form = useDirecionadaForm()
	const {
		setFocus,
		formState: { errors },
	} = form

	const { handleError } = useFormErrorFocus(errors, setFocus)

	const colors = INSPECTION_COLORS.oceanBlue

	const { defaultBranchId, defaultContractId, defaultCustomerId } =
		useProfileStore()
	const { data: branchOptions } = useOrganization()
	const { data: quizList } = useQuizListByBranchId(defaultBranchId)

	const { mutateAsync, isPending } = useCreateDirecionada()

	// Context fields (persisted in zustand)
	const branchId = defaultBranchId
	const crId = defaultContractId

	const selectedBranch = branchOptions?.find((b) => b.id === branchId)
	const branchName = selectedBranch?.name || ''

	const QUESTIONS_SELECT =
		quizList?.map((quiz) => ({
			id: quiz.id,
			name: quiz.title,
			kind: quiz.kind,
			questions: quiz.questions.map((q) => ({
				id: q.id,
				title: q.question,
				imagePath: q.imagePath,
			})),
		})) || ([] as Questionnaire[])

	const { data: employeeData } = usePeople()

	// Questionnaire
	const selectedQuestionnaire = form.watch('quizId')

	const activeQuestionnaire = QUESTIONS_SELECT.find(
		(q) => q.id === selectedQuestionnaire,
	)

	const handleQuestionnaireChange = (
		value: string,
		onChange?: (value: string) => void,
	) => {
		onChange?.(value)
		form.setValue('quizId', value, {
			shouldDirty: true,
			shouldValidate: true,
		})
		const questionnaire = QUESTIONS_SELECT.find((q) => q.id === value)
		if (!questionnaire) {
			form.setValue('answers', [])
			return
		}

		// resposta livre começa com texto vazio: é o que diferencia o campo de texto do checklist
		const initialAnswers: DirecionadaInput['answers'] =
			questionnaire.questions.map((question) => ({
				quizQuestionId: question.id,
				...(questionnaire.kind === 'FREE_TEXT' ? { textAnswer: '' } : {}),
			}))

		form.setValue('answers', initialAnswers, {
			shouldDirty: true,
			shouldValidate: true,
		})
	}

	const handleCancel = () => {
		navigate(ROUTES.INSPECTIONS.DIRECTED.INDEX)
	}

	const handleSubmit = async (formData: DirecionadaFormData) => {
		if (!crId || !formData.date || !formData.answers) {
			toast.error(t('inspections.directed.validationError'))
			return
		}

		if (
			formData.answers.some(
				(answer) => !answer.answer && answer.textAnswer === undefined,
			)
		) {
			toast.error(t('inspections.directed.validationError'))
			return
		}

		const selectedCr = selectedBranch?.crs?.find((c) => c.id === crId)

		// require customer when CR has customers
		if ((selectedCr?.customers?.length ?? 0) > 0 && !defaultCustomerId) {
			toast.error(
				t('inspections.common.selectCustomerRequired') ||
					'Selecione um cliente',
			)
			return
		}

		try {
			const answersPayload = formData.answers.map((answer) => ({
				...answer,
				...(answer.textAnswer !== undefined
					? { textAnswer: answer.textAnswer.trim() }
					: {}),
				...(answer.image instanceof File ? { image: answer.image } : {}),
				...(answer.dueDate
					? {
							dueDate: (() => {
								const parsed = parseStringToDateForDisplay(answer.dueDate)
								return parsed ? formatDateYMD(parsed) : answer.dueDate
							})(),
						}
					: {}),
			}))

			const payload = {
				crId,
				...(defaultCustomerId ? { customerId: defaultCustomerId } : {}),
				quizId: formData.quizId,
				date: formData.date,
				...(formData.comment ? { comment: formData.comment } : {}),
				answers: answersPayload,
			}

			await mutateAsync(payload)
			toast.success(t('inspections.directed.saveSuccess'))
			navigate(ROUTES.INSPECTIONS.DIRECTED.INDEX)
		} catch (error) {
			toast.error(
				isPayloadTooLargeApiError(error)
					? t('errors.payload_too_large')
					: getApiErrorMessage(error),
			)
		}
	}

	return (
		<InspectionCard colors={colors}>
			<InspectionCardHeader
				icon={<ClipboardCheck className="size-5" />}
				title={t('inspections.types.directed.title')}
				colors={colors}
				onBack={handleCancel}
				branch={branchName}
				branchId={branchId}
				branchList={branchOptions}
				crId={crId}
				crList={selectedBranch?.crs}
			/>

			{/* ── Form ── */}
			<Form {...form}>
				<form
					onSubmit={form.handleSubmit(handleSubmit, handleError)}
					className="flex flex-1 flex-col"
				>
					<CardContent className="flex flex-col gap-6 px-2 py-5 sm:px-6">
						{/* ── Context: Date + Questionnaire ── */}
						<div className="grid grid-cols-1 sm:grid-cols-2 px-2 gap-4 sm:gap-6">
							<FormField
								name="date"
								control={form.control}
								render={({ field }) => (
									<FormItem
										label={t('inspections.common.date')}
										error={form.formState.errors.date?.message}
									>
										<InspectionDateField
											value={field.value ? new Date(field.value) : undefined}
											onChange={(date) =>
												field.onChange(date ? date.toISOString() : '')
											}
										/>
									</FormItem>
								)}
							/>

							{/* Questionnaire */}
							<FormField
								name="quizId"
								control={form.control}
								render={({ field }) => (
									<FormItem
										label={t('inspections.directed.questionnaire')}
										error={form.formState.errors.quizId?.message}
									>
										<FieldContent fieldName={field.name}>
											<NativeSelect
												name={field.name}
												ref={field.ref}
												value={field.value}
												onChange={(e) =>
													handleQuestionnaireChange(
														e.target.value,
														field.onChange,
													)
												}
												className="w-full min-w-40 truncate"
											>
												<NativeSelectOption value="" disabled>
													{t('inspections.directed.selectQuestionnaire')}
												</NativeSelectOption>
												{QUESTIONS_SELECT.map((q) => (
													<NativeSelectOption key={q.id} value={q.id}>
														{q.kind === 'FREE_TEXT'
															? `${q.name} (${t('inspections.directed.freeTextTag')})`
															: q.name}
													</NativeSelectOption>
												))}
											</NativeSelect>
										</FieldContent>
									</FormItem>
								)}
							/>
						</div>

						{/* ── Questions — visible when a questionnaire is selected ── */}
						{activeQuestionnaire && (
							<>
								<Separator className="bg-border/60" />

								<div className="space-y-3 animate-in fade-in slide-in-from-top-2 duration-200">
									<div className="flex items-center justify-between">
										<h2 className="text-base font-semibold text-kaefer-dark-grey">
											{activeQuestionnaire.name}
										</h2>
									</div>

									<FormField
										name="answers"
										control={form.control}
										render={({ field }) => (
											<FormItem
												label=""
												error={form.formState.errors.answers?.message}
											>
												<FieldContent fieldName={field.name}>
													<div className="space-y-3">
														{employeeData &&
															activeQuestionnaire.questions.map(
																(question, idx) =>
																	activeQuestionnaire.kind === 'FREE_TEXT' ? (
																		<DirectedFreeTextQuestionCard
																			key={question.id}
																			question={question}
																			index={idx + 1}
																			answerIndex={idx}
																		/>
																	) : (
																		<DirectedQuestionCard
																			key={question.id}
																			question={question}
																			index={idx + 1}
																			answerIndex={idx}
																			responsibles={employeeData}
																		/>
																	),
															)}
													</div>
												</FieldContent>
											</FormItem>
										)}
									/>
								</div>
							</>
						)}

						<FormField
							name="comment"
							control={form.control}
							render={({ field }) => (
								<FormItem
									label={t('inspections.directed.comment')}
									error={form.formState.errors.comment?.message}
								>
									<Textarea
										{...field}
										placeholder={t('inspections.directed.commentPlaceholder')}
									/>
								</FormItem>
							)}
						/>
					</CardContent>

					<InspectionCardFooter
						colors={colors}
						isSubmitting={isPending}
						onCancel={handleCancel}
						cancelLabel={t('inspections.directed.cancel')}
						saveLabel={t('inspections.directed.save')}
						savingLabel={t('inspections.directed.saving')}
					/>
				</form>
			</Form>
		</InspectionCard>
	)
}
